import {
  queryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import bodycamClient from "@/shared/api/clients/bodycamClient";
import { useToast } from "@/shared/toast";

/** One recipient of a video share, as returned by GET /evidence/{id}/shares. */
export interface EvidenceShare {
  sharedWithUserId: string;
  createdBy: string;
  /** Epoch milliseconds. */
  createdAt: number;
}

export interface EvidenceSharesResponse {
  evidenceId: string;
  shares: EvidenceShare[];
}

interface ShareEvidenceResponse {
  evidenceId: string;
  shared: string[];
}

interface UnshareEvidenceResponse {
  evidenceId: string;
  unshared: string[];
}

/** The share endpoints accept at most 50 userIds per request. */
export const MAX_SHARE_RECIPIENTS = 50;

/** Optional note included with a share; capped to keep it a short message. */
export const MAX_SHARE_MESSAGE_LENGTH = 500;

/** Variables for the share mutations: recipients plus an optional note. */
export interface ShareEvidenceVars {
  userIds: string[];
  /** Optional free-text note sent to the recipients. */
  message?: string;
}

export const evidenceShareKeys = {
  all: ["evidenceShares"] as const,
  list: (evidenceId: string) => [...evidenceShareKeys.all, evidenceId] as const,
};

export async function fetchEvidenceShares(
  evidenceId: string,
  signal?: AbortSignal,
): Promise<EvidenceSharesResponse> {
  const response = await bodycamClient.get<EvidenceSharesResponse>(
    `/evidence/${encodeURIComponent(evidenceId)}/shares`,
    { signal },
  );
  return response.data;
}

export function evidenceSharesQuery(evidenceId: string) {
  return queryOptions<EvidenceSharesResponse>({
    queryKey: evidenceShareKeys.list(evidenceId),
    queryFn: ({ signal }) => fetchEvidenceShares(evidenceId, signal),
    // The list is edited live from the share dialog; always refetch on open.
    staleTime: 0,
    retry: false,
  });
}

export async function shareEvidence(
  evidenceId: string,
  userIds: string[],
  message?: string,
): Promise<ShareEvidenceResponse> {
  const trimmed = message?.trim();
  const response = await bodycamClient.post<ShareEvidenceResponse>(
    `/evidence/${encodeURIComponent(evidenceId)}/share`,
    { userIds, ...(trimmed ? { message: trimmed } : {}) },
  );
  return response.data;
}

export async function unshareEvidence(
  evidenceId: string,
  userIds: string[],
): Promise<UnshareEvidenceResponse> {
  const response = await bodycamClient.delete<UnshareEvidenceResponse>(
    `/evidence/${encodeURIComponent(evidenceId)}/share`,
    { data: { userIds } },
  );
  return response.data;
}

/** Share-API error bodies are always { message: string }. */
function errorMessage(err: unknown, fallback: string): string {
  const e = err as {
    response?: { data?: { message?: string } };
    message?: string;
  };
  return e?.response?.data?.message || e?.message || fallback;
}

export function useShareEvidence(evidenceId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: ({ userIds, message }: ShareEvidenceVars) =>
      shareEvidence(evidenceId, userIds, message),
    onSuccess: (res) => {
      const n = res.shared.length;
      toast.success(n === 1 ? "Video shared with 1 user" : `Video shared with ${n} users`);
    },
    onError: (err) => toast.error(errorMessage(err, "Failed to share video.")),
    // Refetch even on error — a partial-failure body still changes nothing,
    // but the server list is the only source of truth for what's shared.
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: evidenceShareKeys.list(evidenceId),
      }),
  });
}

/** Outcome of a batch share across several videos. */
export interface ShareMultipleResult {
  /** evidenceIds that were shared successfully. */
  succeeded: string[];
  /** evidenceIds that failed, with the server/error message. */
  failed: { evidenceId: string; message: string }[];
}

/**
 * Shares one set of recipients with several videos at once (the "Share all"
 * action over a selection). Each video is a separate request; a partial
 * failure still reports which videos went through. Every affected share list
 * is invalidated so an open single-video dialog reflects the change.
 */
export function useShareMultipleEvidence(evidenceIds: string[]) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async ({
      userIds,
      message,
    }: ShareEvidenceVars): Promise<ShareMultipleResult> => {
      const results = await Promise.allSettled(
        evidenceIds.map((id) => shareEvidence(id, userIds, message)),
      );
      const succeeded: string[] = [];
      const failed: ShareMultipleResult["failed"] = [];
      results.forEach((r, i) => {
        if (r.status === "fulfilled") {
          succeeded.push(evidenceIds[i]);
        } else {
          failed.push({
            evidenceId: evidenceIds[i],
            message: errorMessage(r.reason, "Failed to share video."),
          });
        }
      });
      return { succeeded, failed };
    },
    onSuccess: (res, { userIds }) => {
      const nUsers = userIds.length;
      const nVideos = res.succeeded.length;
      if (nVideos > 0) {
        const videoLabel = nVideos === 1 ? "1 video" : `${nVideos} videos`;
        const userLabel = nUsers === 1 ? "1 user" : `${nUsers} users`;
        toast.success(`Shared ${videoLabel} with ${userLabel}`);
      }
      if (res.failed.length > 0) {
        const n = res.failed.length;
        toast.error(
          n === 1
            ? "Couldn't share 1 video."
            : `Couldn't share ${n} videos.`,
        );
      }
    },
    onError: (err) => toast.error(errorMessage(err, "Failed to share videos.")),
    onSettled: () => {
      for (const id of evidenceIds) {
        queryClient.invalidateQueries({
          queryKey: evidenceShareKeys.list(id),
        });
      }
    },
  });
}

export function useUnshareEvidence(evidenceId: string) {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: (userIds: string[]) => unshareEvidence(evidenceId, userIds),
    onSuccess: () => toast.success("Access removed"),
    onError: (err) =>
      toast.error(errorMessage(err, "Failed to remove access.")),
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: evidenceShareKeys.list(evidenceId),
      }),
  });
}
